import { AlertTriangle } from 'lucide-react';
import {
  TIMING_TONE,
  WHEN_LABELS,
  type V2ActionStatus,
  type V2AssessmentReport,
  type V2EscalationBanner,
  type V2ReportNeed,
  type V2ReportPillar,
} from '../api/v2ReportApi';
import { SafeLink } from './SupportItems';
import { V2NeedDetail } from './V2NeedDetail';
import { V2TimingGroups } from './V2TimingGroups';

const GREEN = '#026857';

/**
 * MVP 2 client report (Feature 110; laid out after Brooks's prototype in 128).
 *
 * Escalation banner → score → "What matters most right now" (the top 3, open)
 * → "Everything else, by timing" (fixed timing headings, each need folded to
 * its title). The score says
 * where the household stands and the priorities say what matters now, so the
 * pillar bars carry no "this one is a gap" highlight. Every rule — which needs,
 * in what order, which sections, the banner — is decided server-side; this only
 * draws it, so the screen and the PDF cannot disagree.
 *
 * Self-contained by design: the V1 report keeps its own copy of the gauge so
 * the two versions can be restyled independently.
 */

function Gauge({ score }: { score: number }) {
  const r = 54;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <svg
      width="140"
      height="140"
      viewBox="0 0 140 140"
      role="img"
      aria-label={`Score ${score} of 100`}
    >
      <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="12" />
      <circle
        cx="70"
        cy="70"
        r={r}
        fill="none"
        stroke="#f3c66c"
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped / 100)}
        transform="rotate(-90 70 70)"
      />
      <text x="70" y="70" textAnchor="middle" style={{ font: '600 34px serif', fill: '#ffffff' }}>
        {score}
      </text>
      <text
        x="70"
        y="90"
        textAnchor="middle"
        style={{ font: '400 11px sans-serif', fill: 'rgba(246,231,196,0.7)' }}
      >
        out of 100
      </text>
    </svg>
  );
}

export function PillarBar({ pillar }: { pillar: V2ReportPillar }) {
  // Every pillar always scores: Not Applicable was cut in review, so none can
  // drop out of the average or arrive without a bar.
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-sm">
        <span className="text-gray-800">{pillar.name}</span>
        <span className="text-gray-500">{pillar.score}%</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full"
          style={{ width: `${pillar.score}%`, background: GREEN }}
        />
      </div>
    </div>
  );
}

/**
 * The matrix's escalation copy: a critical situation takes all the attention.
 * Titled "First, today" (matrix C3) so it never reads as the Now timing group.
 */
function EscalationBanner({ banner }: { banner: V2EscalationBanner }) {
  return (
    <section
      aria-label="First, today"
      className="flex flex-col gap-4 rounded-2xl border border-gray-200 border-t-4 border-t-[#e0a53c] bg-white p-6 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="max-w-xl">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#b3413e]">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          First, today
        </div>
        <p className="mt-2 text-[15px] leading-relaxed text-gray-800">{banner.message}</p>
      </div>
      <SafeLink
        href={banner.href}
        className="shrink-0 rounded-full bg-[#f3c66c] px-5 py-2.5 text-center text-sm font-semibold text-[#04332b] hover:bg-[#e9b54f]"
      >
        {banner.action}
      </SafeLink>
    </section>
  );
}

function TopPriority({
  need,
  rank,
  onStatusChange,
  disabled,
}: {
  need: V2ReportNeed;
  rank: number;
  onStatusChange?: (needId: string, status: V2ActionStatus) => void;
  disabled: boolean;
}) {
  return (
    <article className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
      <header className="flex items-start gap-4">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f6e7c4] font-serif text-[15px] font-semibold text-[#04332b]"
          aria-label={`Priority ${rank}`}
        >
          {rank}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-serif text-[19px] font-semibold text-gray-900">{need.title}</h3>
            <span
              className="whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium"
              style={TIMING_TONE[need.when]}
            >
              {WHEN_LABELS[need.when]}
            </span>
          </div>
          <p className="mt-0.5 text-[13px] text-gray-500">
            {need.pillarName} · {need.readinessLabel}
          </p>
        </div>
      </header>
      <div className="mt-4 sm:pl-12">
        <V2NeedDetail need={need} onStatusChange={onStatusChange} disabled={disabled} />
      </div>
    </article>
  );
}

export function V2ReportView({
  report,
  onStatusChange,
  savingNeedId,
}: {
  report: V2AssessmentReport;
  /**
   * Omitted when the viewer is the advisor: they see where the client is, and
   * cannot move it on their behalf — the backend refuses it either way.
   */
  onStatusChange?: (needId: string, status: V2ActionStatus) => void;
  /** The need mid-save, so only that one's buttons are disabled. */
  savingNeedId?: string | null;
}) {
  const setStatus = report.viewerIsClient ? onStatusChange : undefined;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-8">
      {report.escalationBanner && <EscalationBanner banner={report.escalationBanner} />}

      <section className="rounded-2xl bg-gradient-to-br from-[#04332b] via-[#0d5d4e] to-[#1a7d6c] p-8 text-center">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#f6e7c4]/60">
          Overall Readiness
        </div>
        <div className="mt-3 flex justify-center">
          <Gauge score={report.overallScore} />
        </div>
        <h1 className="mt-4 font-serif text-2xl text-white">{report.categoryLabel}</h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#f6e7c4]/85">
          {report.categoryDescription}
        </p>
        <p className="mt-4 text-[11px] uppercase tracking-wider text-[#f6e7c4]/50">
          Prepared for {report.clientName}
        </p>
        {report.about && (
          <p className="mt-1 text-[11px] uppercase tracking-wider text-[#f6e7c4]/50">
            About {report.about}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <h2 className="mb-1 font-serif text-xl text-gray-900">Five pillars of readiness</h2>
        <p className="mb-4 text-sm leading-relaxed text-gray-500">
          Your readiness score shows how much planning you have completed. Your priorities consider
          your circumstances, preferences and timing to identify where action could have the
          greatest impact.
        </p>
        <div className="space-y-4">
          {report.pillars.map((pillar) => (
            <PillarBar key={pillar.key} pillar={pillar} />
          ))}
        </div>
      </section>

      {report.topPriorities.length === 0 ? (
        <p className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
          No open priorities right now
        </p>
      ) : (
        <section aria-label="What matters most right now" className="space-y-4">
          <div>
            <h2 className="font-serif text-xl text-gray-900">What matters most right now</h2>
            <p className="mt-1 text-sm text-gray-500">
              Chosen from what is still unresolved and ranked by your situation — not by which
              pillar scored lowest.
            </p>
          </div>
          {report.topPriorities.map((need, i) => (
            <TopPriority
              key={need.needId}
              need={need}
              rank={i + 1}
              onStatusChange={setStatus}
              disabled={savingNeedId === need.needId}
            />
          ))}
        </section>
      )}

      {report.timingGroups.length > 0 && (
        <section className="space-y-4">
          <div>
            <h2 className="font-serif text-xl text-gray-900">Everything else, by timing</h2>
            <p className="mt-1 text-sm text-gray-500">Open any need for the full detail.</p>
          </div>
          <V2TimingGroups groups={report.timingGroups} />
        </section>
      )}

      <p className="text-center text-xs text-gray-600">{report.timingNote}</p>
    </div>
  );
}
